import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore'
import { db } from './config'

const USER_NOTES_COLLECTION = 'userNotes'

/**
 * Subscribe to the signed-in user's visible (non-hidden) notes.
 * @param {string} ownerUid
 * @param {(notes: Array<{id: string, title?: string, body?: string, owner: string, hidden?: boolean, createdAt?, updatedAt?}>) => void} callback
 * @returns {() => void}
 */
export function subscribeToMyNotes(ownerUid, callback) {
  const q = query(
    collection(db, USER_NOTES_COLLECTION),
    where('owner', '==', ownerUid),
    where('hidden', '==', false),
    orderBy('updatedAt', 'desc')
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const notes = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
      callback(notes)
    },
    (err) => {
      console.error('Error subscribing to notes:', err)
    }
  )
}

/**
 * Subscribe to every user note (including soft-deleted). Firestore rules: admin only.
 * @param {(notes: Array<{id: string, title?: string, body?: string, owner: string, hidden?: boolean, createdAt?, updatedAt?}>) => void} callback
 * @returns {() => void}
 */
export function subscribeToAllUserNotes(callback) {
  const q = query(
    collection(db, USER_NOTES_COLLECTION),
    orderBy('updatedAt', 'desc')
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const notes = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
      callback(notes)
    },
    (err) => {
      console.error('Error subscribing to all notes:', err)
    }
  )
}

/**
 * @param {string} ownerUid
 * @param {{ title?: string, body?: string }} data
 */
export async function createUserNote(ownerUid, data = {}) {
  const docRef = await addDoc(collection(db, USER_NOTES_COLLECTION), {
    owner: ownerUid,
    title: (data.title ?? 'Untitled note').slice(0, 500),
    body: typeof data.body === 'string' ? data.body : '',
    hidden: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  })
  return docRef.id
}

/**
 * @param {string} noteId
 * @param {{ title?: string, body?: string }} updates
 */
export async function updateUserNote(noteId, updates) {
  const ref = doc(db, USER_NOTES_COLLECTION, noteId)
  const payload = { updatedAt: serverTimestamp() }
  if (typeof updates.title === 'string') payload.title = updates.title.slice(0, 500)
  if (typeof updates.body === 'string') payload.body = updates.body
  await updateDoc(ref, payload)
}

/**
 * Soft-delete: marks the note hidden. User cannot read it afterward (rules + queries).
 * @param {string} noteId
 */
export async function softDeleteUserNote(noteId) {
  const ref = doc(db, USER_NOTES_COLLECTION, noteId)
  await updateDoc(ref, {
    hidden: true,
    updatedAt: serverTimestamp()
  })
}
